import React from "react";
import { Typography, Button } from "@material-ui/core";
import { withRouter } from "react-router-dom";
import { i18n } from "../../translate/i18n";

// Boundary de RUTAS: envuelve TODAS las pantallas privadas de WAIA (ver
// `routes/index.js`). Es uno solo para todas, y eso tiene una consecuencia que
// costó cara el 23-sep:
//
//   ⚠ Un boundary de React NO se recupera solo. Una vez que `hasError` queda en
//   true, se queda ahí. Como este envuelve las rutas de a una sola instancia,
//   un crash en `/` dejaba «Algo salió mal» TAMBIÉN en Chats, en Contactos y en
//   todo lo demás: la URL cambiaba, el contenido no. Verificado con el bundle
//   deployado (`index-e8b2c9b9.js`) el 24-09-2026: `/`, `/contacts` y
//   `/tickets` mostraban los tres la misma tarjeta de error.
//
// Por eso el reset al cambiar de ruta: el bug de UNA pantalla tiene que quedar
// en esa pantalla. Si la ruta nueva también tira, se vuelve a ver la tarjeta —
// no hay loop, porque el reset sólo ocurre cuando el pathname cambia.

const safeT = (key, fallback) => {
  try {
    const out = i18n.t(key);
    return !out || out === key ? fallback : out;
  } catch {
    return fallback;
  }
};

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary/rutas] ${error?.name || "Error"}: ${error?.message || error}`,
      {
        path: this.props.location?.pathname,
        componentStack: info?.componentStack,
        error
      }
    );
  }

  componentDidUpdate(prevProps) {
    if (
      this.state.hasError &&
      prevProps.location?.pathname !== this.props.location?.pathname
    ) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 32, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>
            {safeT("errorBoundary.routeTitle", "Esta pantalla no se pudo mostrar")}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {safeT(
              "errorBoundary.routeHelp",
              "Probá con otra sección del menú o recargá la página."
            )}
          </Typography>
          {/* El detalle técnico se deja a la vista a propósito: es lo que
              permitió identificar `c.map is not a function` sin acceso a la
              consola del cliente. Va en chico y en gris, no como título. */}
          <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 16 }}>
            {this.state.error?.message}
          </Typography>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              {safeT("errorBoundary.retry", "Reintentar")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => window.location.reload()}
            >
              {safeT("errorBoundary.reload", "Recargar la página")}
            </Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default withRouter(ErrorBoundary);
